// rce product init — product / category / global carousels.
//
// For each carousel whose per-carousel predicate resolves true (default true),
// dispatch on `type` to the matching static draw(). The draw() calls + arg order
// are verified correct (PLAN.md rce signatures; iter 01). Each carousel is
// isolated in its own try/catch so one bad entry (e.g. a missing
// targetContainerSelector, whose ctor throws) can't skip the others, and each
// activated carousel's instanceId (draw()'s `string | null` return) is collected.
//
// ANALYTICS LINK (no wiring needed): rce integrates analytics on-by-default via
// `createPusher(!disableAnalyticsIntegration, analyticsEventsTarget ?? 'raventicLayer',
// eventPusher?)` → the shared raventicLayer (or dataLayer), so no explicit
// `eventPusher` has to be threaded between rce and analytics.
//
// LATE ACTIVATION (iter 13): `activateWhen` may also be async or a trigger. Then the
// carousel is NOT drawn here; startActivation() (../activation-runtime.ts) starts the
// deferred work and later runs the SAME drawCarousel() with the fire's context, so the
// id getters see `ctx.trigger`. Such carousels are absent from `instances` and report
// through `onActivation` instead. Sync predicates behave exactly as before.
//
// Late draws differ from boot draws in two ways, because real rce's draw() fetches
// ASYNCHRONOUSLY — it calls a lazy id getter inside its async drawWidget, swallows
// failures into its own errorHandler and returns an instanceId anyway:
//   - the id getter is called EAGERLY by the facade with the fire's ctx and rce gets
//     the resolved value, so a throwing getter is an `error` event (not a silent rce
//     failure), and a `null` instanceId (prepare failed) is an `error` event too;
//   - the carousel's errorHandler is wrapped so an `execute`-stage failure is ALSO
//     forwarded as an `error` event (after `drawn`, which means "draw started").
// Boot-time draws keep the lazy `() => getter(ctx)` closure and the customer's own
// errorHandler untouched — today's behaviour.
//
// HIDE: a carousel's `hide` (../hide.ts) is compiled and appended to a COPY of its
// `widgetConfig.customPageStyles` (withHide) inside the carousel's try/catch, BEFORE
// startActivation(): an invalid rule is that carousel's boot error and it is never drawn (nor
// subscribed). rce injects `customPageStyles` only once a draw has products — until then the
// native block stays visible — never removes it, and deduplicates it by content: a carousel
// drawn on every event (`once: false`) adds its rules once, and a later EMPTY draw leaves the
// native block hidden for the rest of the page view (reviewed and accepted as is, iteration
// 17). The customer's config object is never mutated.

import type {
  RaventicSdkConfig,
  ProductRceCallbacks,
  CategoryRceCallbacks,
  GlobalRceCallbacks,
  ProductIdSource,
  CategoryIdSource,
} from "../config";
import type { ActivationContext } from "../activation";
import type { ActivationReporter } from "../activation-runtime";
import { startActivation } from "../activation-runtime";
import { withHide } from "../hide";
import {
  RaventicRCECarousel,
  RaventicCategoryRCECarousel,
  RaventicGlobalRCECarousel,
  setDebug,
} from "@raventic/sdk-rce2-v1";

type RceSection = NonNullable<RaventicSdkConfig["rce"]>;

export interface RceInitError {
  index: number;
  type: string;
  error: unknown;
}

export interface RceInitResult {
  // One entry per ACTIVATED (predicate-passing) carousel that drew without
  // throwing; instanceId is draw()'s return (null when prepare failed but did not
  // throw). Failures that threw are in `errors` instead.
  instances: Array<{
    index: number;
    type: RceSection["carousels"][number]["type"];
    instanceId: string | null;
  }>;
  errors: RceInitError[];
}

// One step per carousel (iter 23): boot() runs each in its own pipeline after that carousel's
// readiness (today: the shared DOM wait), so no carousel waits for another widget. Each draws
// its carousel when activated, filling `result` in place (instances + per-carousel errors, the
// latter also reported to `onError` for BootResult). A step never throws. Late (async /
// trigger) carousels report through `report` (boot passes the one wrapping
// config.onActivation).
export function rcePipelines(
  cfg: RceSection,
  // The page context, read when a step runs (built at DOM readiness by boot()).
  ctxAt: () => ActivationContext,
  report: ActivationReporter,
  result: RceInitResult,
  onError: (index: number, type: string, error: unknown) => void = () => undefined,
): Array<() => void> {
  // Info logging (sdk-rce2-v1 2.1.0 setDebug; module-global, off by default in its library
  // build) — same rule as analytics: explicit config wins, else on only outside production
  // builds (`process.env.NODE_ENV` is baked by the customer build's define map).
  setDebug(cfg.debug ?? process.env.NODE_ENV !== "production");

  return cfg.carousels.map((carousel, index) => () => {
    // The whole per-carousel body — including the customer-supplied activateWhen
    // predicate (and a trigger's subscribe) — runs inside the try, so a throwing
    // predicate is isolated to this carousel.
    try {
      const ctx = ctxAt();
      // `hide` merged first (see the header): the carousel every draw below uses.
      const drawable = withCarouselHide(carousel);
      const drawNow = startActivation(
        carousel.activateWhen,
        ctx,
        {
          widget: `rce[${index}:${carousel.type}]`,
          container: carousel.widgetConfig.targetContainerSelector,
          containerTimeout: carousel.containerTimeout,
          draw: (lateCtx, fail) => {
            const instanceId = drawCarousel(drawable, lateCtx, fail);
            if (instanceId === null) throw new Error("rce draw() returned null (prepare failed)");
            return instanceId;
          },
        },
        report,
      );
      if (!drawNow) return;

      // Callbacks are read per-carousel INSIDE drawCarousel, where `carousel.type`
      // narrows the discriminated union to the variant's own callbacks type.
      const instanceId = drawCarousel(drawable, ctx);
      result.instances.push({ index, type: carousel.type, instanceId });
    } catch (error) {
      result.errors.push({ index, type: carousel.type, error });
      onError(index, carousel.type, error);
      console.error(
        `[@raventic/sdk] rce carousel #${index} (${carousel.type}) failed`,
        error,
      );
    }
  });
}

// The carousel with its `hide` appended to a copy of `widgetConfig.customPageStyles`; the
// carousel itself when it hides nothing. Throws on an invalid rule (../hide.ts).
function withCarouselHide<C extends RceSection["carousels"][number]>(carousel: C): C {
  const widgetConfig = withHide(carousel.widgetConfig, carousel.hide);
  return widgetConfig === carousel.widgetConfig ? carousel : { ...carousel, widgetConfig };
}

// Boot draw (no `fail`): a ctx-taking id getter becomes the zero-arg getter rce
// expects, bound to this draw's context (rce calls it lazily, when it fetches).
// Late draw (`fail` given): the getter is called NOW, so a throw surfaces here.
function bindId(
  id: ProductIdSource | CategoryIdSource,
  ctx: ActivationContext,
  fail: ((error: unknown) => void) | undefined,
): string | string[] | (() => string | string[]) {
  if (typeof id !== "function") return id;
  return fail ? id(ctx) : () => id(ctx);
}

// Late draw: forward rce's execute-stage failures (ErrorHandler(stage, error, …)) to
// `fail` as well, after the customer's own handler. Boot draw: the handler as-is.
function forwardErrors<H extends (...args: any[]) => void>(
  handler: H | undefined,
  fail: ((error: unknown) => void) | undefined,
): H | undefined {
  if (!fail) return handler;
  return ((...args: unknown[]) => {
    try {
      handler?.(...args);
    } finally {
      if (args[0] === "execute") fail(new Error(`rce execute failed: ${String(args[1])}`));
    }
  }) as H;
}

function drawCarousel(
  carousel: RceSection["carousels"][number],
  ctx: ActivationContext,
  fail?: (error: unknown) => void,
): string | null {
  switch (carousel.type) {
    case "product": {
      // RaventicRCECarousel.draw(rceConfig, widgetConfig, productId, onProductClick?,
      //   errorHandler?, resultHandler?, impressionHandler?, eventPusher?): string | null
      // The custom card (widgetConfig.card/cardOptions) rides inside `widgetConfig` — forwarded as-is.
      const cb: ProductRceCallbacks = carousel.callbacks ?? {};
      return RaventicRCECarousel.draw(
        carousel.rceConfig,
        carousel.widgetConfig,
        bindId(carousel.productId, ctx, fail),
        cb.onProductClick,
        forwardErrors(cb.errorHandler, fail),
        cb.resultHandler,
        cb.impressionHandler,
        cb.eventPusher,
      );
    }

    case "category": {
      // RaventicCategoryRCECarousel.draw(rceConfig, widgetConfig, categoryId, ...callbacks)
      // The custom card (widgetConfig.card/cardOptions) rides inside `widgetConfig` — forwarded as-is.
      const cb: CategoryRceCallbacks = carousel.callbacks ?? {};
      return RaventicCategoryRCECarousel.draw(
        carousel.rceConfig,
        carousel.widgetConfig,
        bindId(carousel.categoryId, ctx, fail),
        cb.onProductClick,
        forwardErrors(cb.errorHandler, fail),
        cb.resultHandler,
        cb.impressionHandler,
        cb.eventPusher,
      );
    }

    case "global": {
      // RaventicGlobalRCECarousel.draw(rceConfig, widgetConfig, ...callbacks) — no id arg
      // The custom card (widgetConfig.card/cardOptions) rides inside `widgetConfig` — forwarded as-is.
      const cb: GlobalRceCallbacks = carousel.callbacks ?? {};
      return RaventicGlobalRCECarousel.draw(
        carousel.rceConfig,
        carousel.widgetConfig,
        cb.onProductClick,
        forwardErrors(cb.errorHandler, fail),
        cb.resultHandler,
        cb.impressionHandler,
        cb.eventPusher,
      );
    }
  }
}
